import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { AdminReasonRow, CreateReasonRequest } from '@nest-recruit/shared';
import { ApiError, apiGet, apiPatch, apiPost } from '../../../lib/api-client';
import type { RowCache } from '../components/simple-list/use-row-changes';

// ADMIN-07 (S15 Q5; Admin redesign batch 2). Every change shows at once and is sent through the simple list's row
// engine, which queues each row's changes so Undo always carries the latest version (06-qol §4).

export const REASONS_KEY = ['admin', 'reasons'] as const;

export interface ReasonLists {
  readonly rejection: readonly AdminReasonRow[];
  readonly withdrawal: readonly AdminReasonRow[];
}

export function useReasons(isEnabled: boolean): UseQueryResult<ReasonLists, ApiError> {
  return useQuery<ReasonLists, ApiError>({
    queryKey: REASONS_KEY,
    queryFn: () => apiGet<ReasonLists>('/admin/reasons'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

/** Both lists as one run of rows for the row engine; written back split by list, each in its own order. */
export const REASON_ROWS: RowCache<ReasonLists, AdminReasonRow> = {
  queryKey: REASONS_KEY,
  rowsOf: (lists) => [...lists.rejection, ...lists.withdrawal],
  withRows: (_lists, rows) => ({ rejection: rows.filter((row) => row.list === 'rejection'), withdrawal: rows.filter((row) => row.list === 'withdrawal') }),
  idOf: (row) => row.id,
};

interface ReasonAnswer {
  readonly reason: AdminReasonRow;
}

export const reasonsApi = {
  rename: (id: string, label: string) => async (version: number) => (await apiPatch<ReasonAnswer>(`/admin/reasons/${id}`, { version, label })).reason.version,
  setPoolPrompt: (id: string, triggersPool: boolean) => async (version: number) => (await apiPatch<ReasonAnswer>(`/admin/reasons/${id}`, { version, triggersPool })).reason.version,
  retire: (id: string) => async (version: number) => (await apiPost<ReasonAnswer>(`/admin/reasons/${id}/retire`, { version })).reason.version,
  restore: (id: string) => async (version: number) => (await apiPost<ReasonAnswer>(`/admin/reasons/${id}/restore`, { version })).reason.version,
  create: async (input: CreateReasonRequest): Promise<AdminReasonRow> => (await apiPost<ReasonAnswer>('/admin/reasons', input)).reason,
};
